import React, { useState, useEffect } from "react";
import {
  HelpCircle,
  X,
  Plus,
  Trash2,
  CheckCircle,
  XCircle,
  Save,
} from "lucide-react";
import {
  doc,
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "../../context/AuthContext";
import Toast from "../Toast/Toast";

const StratBuilder = () => {
  const { user } = useAuth();
  const [showHelp, setShowHelp] = useState(false);
  const [strategyName, setStrategyName] = useState("");
  const [trades, setTrades] = useState([
    {
      id: Date.now(),
      type: "",
      risk: 0,
      reward: 0,
      result: "win", // "win" or "loss"
      rr: 0,
      timestamp: new Date().toISOString().split("T")[0],
      notes: "",
    },
  ]);
  const [rules, setRules] = useState([""]);
  const [stopLoss, setStopLoss] = useState(2); // Default 2% stop loss
  const [minRR, setMinRR] = useState(1.5); // Default minimum R:R of 1.5
  const [toast, setToast] = useState(null);
  const [savedStrategies, setSavedStrategies] = useState([]);
  const [selectedStrategy, setSelectedStrategy] = useState(null);

  useEffect(() => {
    if (user) {
      fetchStrategies();
    }
  }, [user]);

  const fetchStrategies = async () => {
    try {
      const strategiesRef = collection(db, "strategies");
      const q = query(strategiesRef, where("userId", "==", user.uid));
      const querySnapshot = await getDocs(q);

      const strategies = [];
      querySnapshot.forEach((doc) => {
        strategies.push({ id: doc.id, ...doc.data() });
      });

      setSavedStrategies(strategies);
    } catch (error) {
      console.error("Error fetching strategies:", error);
      setToast({
        type: "error",
        message: "Error loading strategies",
        subText: error.message,
      });
    }
  };

  const handleAddTrade = () => {
    setTrades([
      ...trades,
      {
        id: Date.now(),
        type: "",
        risk: 0,
        reward: 0,
        result: "win",
        rr: 0,
        timestamp: new Date().toISOString().split("T")[0],
        notes: "",
      },
    ]);
  };

  const handleRemoveTrade = (id) => {
    setTrades(trades.filter((trade) => trade.id !== id));
  };

  const handleTradeChange = (id, field, value) => {
    setTrades(
      trades.map((trade) => {
        if (trade.id === id) {
          const updatedTrade = { ...trade, [field]: value };

          // Auto-calculate R:R when risk or reward changes
          if (field === "risk" || field === "reward") {
            const risk =
              field === "risk"
                ? parseFloat(value) || 0
                : parseFloat(trade.risk) || 0;
            const reward =
              field === "reward"
                ? parseFloat(value) || 0
                : parseFloat(trade.reward) || 0;
            updatedTrade.rr = risk > 0 ? (reward / risk).toFixed(2) : 0;
          }

          return updatedTrade;
        }
        return trade;
      })
    );
  };

  const handleAddRule = () => {
    setRules([...rules, ""]);
  };

  const handleRuleChange = (index, value) => {
    const newRules = [...rules];
    newRules[index] = value;
    setRules(newRules);
  };

  const handleRemoveRule = (index) => {
    setRules(rules.filter((_, i) => i !== index));
  };

  const calculateViability = () => {
    // Filter trades based on minimum R:R
    const filteredTrades = trades.filter(
      (trade) => parseFloat(trade.rr) >= minRR || trade.result === "loss"
    );

    if (filteredTrades.length === 0) {
      return {
        totalTrades: 0,
        winningTrades: 0,
        losingTrades: 0,
        winRate: 0,
        avgRR: 0,
        profitFactor: 0,
        isViable: false,
      };
    }

    const winningTrades = filteredTrades.filter(
      (trade) => trade.result === "win"
    );
    const losingTrades = filteredTrades.filter(
      (trade) => trade.result === "loss"
    );

    const winRate = (winningTrades.length / filteredTrades.length) * 100;

    const totalRR = winningTrades.reduce(
      (sum, trade) => sum + parseFloat(trade.rr || 0),
      0
    );
    const avgRR = winningTrades.length > 0 ? totalRR / winningTrades.length : 0;

    // Expected profit factor formula: Win Rate * Avg R:R * (1 - Win Rate)^-1
    const decimalWinRate = winRate / 100;
    const profitFactor = (decimalWinRate * avgRR) / (1 - decimalWinRate);

    // Strategy is viable if profit factor > 1
    const isViable = profitFactor > 1;

    return {
      totalTrades: filteredTrades.length,
      winningTrades: winningTrades.length,
      losingTrades: losingTrades.length,
      winRate: winRate.toFixed(2),
      avgRR: avgRR.toFixed(2),
      profitFactor: profitFactor.toFixed(2),
      isViable,
    };
  };

  const viabilityResults = calculateViability();

  const saveStrategy = async () => {
    if (!strategyName.trim()) {
      setToast({
        type: "error",
        message: "Error",
        subText: "Strategy name is required",
      });
      return;
    }

    try {
      const strategyData = {
        name: strategyName,
        rules: rules.filter((rule) => rule.trim() !== ""),
        trades: trades,
        stopLoss,
        minRR,
        userId: user.uid,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      await addDoc(collection(db, "strategies"), strategyData);

      setToast({
        type: "success",
        message: "Success",
        subText: "Strategy saved successfully",
      });

      fetchStrategies();
    } catch (error) {
      console.error("Error saving strategy:", error);
      setToast({
        type: "error",
        message: "Error",
        subText: "Failed to save strategy",
      });
    }
  };

  const loadStrategy = (strategy) => {
    setStrategyName(strategy.name);
    setRules(strategy.rules || [""]);
    setTrades(strategy.trades || []);
    setStopLoss(strategy.stopLoss || 2);
    setMinRR(strategy.minRR || 1.5);
    setSelectedStrategy(strategy.id);
  };

  const deleteStrategy = async (id) => {
    try {
      await deleteDoc(doc(db, "strategies", id));
      fetchStrategies();

      if (selectedStrategy === id) {
        // Reset form if the deleted strategy was selected
        setStrategyName("");
        setRules([""]);
        setTrades([
          {
            id: Date.now(),
            type: "",
            risk: 0,
            reward: 0,
            result: "win",
            rr: 0,
            timestamp: new Date().toISOString().split("T")[0],
            notes: "",
          },
        ]);
        setStopLoss(2);
        setMinRR(1.5);
        setSelectedStrategy(null);
      }

      setToast({
        type: "success",
        message: "Success",
        subText: "Strategy deleted successfully",
      });
    } catch (error) {
      console.error("Error deleting strategy:", error);
      setToast({
        type: "error",
        message: "Error",
        subText: "Failed to delete strategy",
      });
    }
  };

  return (
    <div className="strategy-builder-container">
      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          subText={toast.subText}
          onClose={() => setToast(null)}
        />
      )}

      <div className="strategy-header">
        <h2>
          <button
            className="help-button"
            onClick={() => setShowHelp(true)}
            aria-label="Help"
          >
            <HelpCircle size={30} />
          </button>
        </h2>
        <div className="save-controls">
          <button className="save-button" onClick={saveStrategy}>
            <Save size={16} />
            Save Strategy
          </button>
        </div>
      </div>

      {showHelp && (
        <div className="help-overlay">
          <div className="help-content">
            <button
              className="close-help"
              onClick={() => setShowHelp(false)}
              aria-label="Close help"
            >
              <X size={20} />
            </button>
            <h3>How to Use the Strategy Builder</h3>
            <div className="help-section">
              <h4>1. Enter Your Strategy Data</h4>
              <p>
                Start by giving your strategy a name. Then, add individual
                trades with their outcomes, risk, and reward values.
              </p>
            </div>
            <div className="help-section">
              <h4>2. Define Your Rules</h4>
              <p>
                Document the specific conditions and rules that define your
                strategy. For example, "Only enter on pullbacks to the 20 EMA"
                or "Exit at 2:1 reward-to-risk ratio."
              </p>
            </div>
            <div className="help-section">
              <h4>3. Set Your Parameters</h4>
              <p>
                Adjust the stop loss percentage and minimum reward-to-risk ratio
                to filter trades and refine your strategy.
              </p>
            </div>
            <div className="help-section">
              <h4>4. Analyze Viability</h4>
              <p>
                The Viability Tool will calculate important metrics like win
                rate and profit factor to determine if your strategy is likely
                to be profitable over time.
              </p>
            </div>
            <div className="help-section">
              <h4>5. Refine Your Strategy</h4>
              <p>
                Add more rules to filter out losing trades or refine your
                entry/exit criteria to improve your strategy's performance.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="strategy-grid">
        <div className="strategy-column strategy-details">
          <div className="strategy-section">
            <label>Strategy Name</label>
            <input
              type="text"
              value={strategyName}
              onChange={(e) => setStrategyName(e.target.value)}
              placeholder="Enter strategy name"
            />
          </div>

          <div className="strategy-section">
            <label>Strategy Rules</label>
            <div className="rules-container">
              {rules.map((rule, index) => (
                <div key={index} className="rule-item">
                  <input
                    type="text"
                    value={rule}
                    onChange={(e) => handleRuleChange(index, e.target.value)}
                    placeholder={`Rule ${index + 1}`}
                  />
                  <button
                    className="remove-rule"
                    onClick={() => handleRemoveRule(index)}
                    aria-label="Remove rule"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
              <button className="add-rule-button" onClick={handleAddRule}>
                <Plus size={16} />
                Add Rule
              </button>
            </div>
          </div>

          <div className="strategy-section parameters">
            <div className="parameter-item">
              <label>Stop Loss (%)</label>
              <input
                type="number"
                min="0.1"
                step="0.1"
                value={stopLoss}
                onChange={(e) => setStopLoss(parseFloat(e.target.value))}
              />
            </div>
            <div className="parameter-item">
              <label>Minimum R:R</label>
              <input
                type="number"
                min="0.1"
                step="0.1"
                value={minRR}
                onChange={(e) => setMinRR(parseFloat(e.target.value))}
              />
            </div>
          </div>

          <div className="viability-tool">
            <h3>Viability Tool</h3>
            <div className="viability-status">
              <div
                className={`status-indicator ${
                  viabilityResults.isViable ? "viable" : "not-viable"
                }`}
              >
                {viabilityResults.isViable ? (
                  <>
                    <CheckCircle size={24} />
                    <span>Viable</span>
                  </>
                ) : (
                  <>
                    <XCircle size={24} />
                    <span>Not Viable</span>
                  </>
                )}
              </div>
            </div>
            <div className="viability-metrics">
              <div className="metric">
                <span className="metric-label">Total Trades:</span>
                <span className="metric-value">
                  {viabilityResults.totalTrades}
                </span>
              </div>
              <div className="metric">
                <span className="metric-label">Win Rate:</span>
                <span className="metric-value">
                  {viabilityResults.winRate}%
                </span>
              </div>
              <div className="metric">
                <span className="metric-label">Average R:R:</span>
                <span className="metric-value">{viabilityResults.avgRR}</span>
              </div>
              <div className="metric">
                <span className="metric-label">Profit Factor:</span>
                <span
                  className={`metric-value ${
                    parseFloat(viabilityResults.profitFactor) > 1
                      ? "profit"
                      : "loss"
                  }`}
                >
                  {viabilityResults.profitFactor}
                </span>
              </div>
              <div className="metric">
                <span className="metric-label">Winning Trades:</span>
                <span className="metric-value profit">
                  {viabilityResults.winningTrades}
                </span>
              </div>
              <div className="metric">
                <span className="metric-label">Losing Trades:</span>
                <span className="metric-value loss">
                  {viabilityResults.losingTrades}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="strategy-column trade-inputs">
          <div className="strategy-section saved-strategies">
            <h3>Saved Strategies</h3>
            <div className="strategies-list">
              {savedStrategies.length === 0 ? (
                <p className="no-strategies">No saved strategies yet</p>
              ) : (
                savedStrategies.map((strategy) => (
                  <div
                    key={strategy.id}
                    className={`strategy-item ${
                      selectedStrategy === strategy.id ? "selected" : ""
                    }`}
                  >
                    <span onClick={() => loadStrategy(strategy)}>
                      {strategy.name}
                    </span>
                    <button
                      className="delete-strategy"
                      onClick={() => deleteStrategy(strategy.id)}
                      aria-label="Delete strategy"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="trade-table">
            <div className="table-header">
              <h3>Trade Inputs</h3>
              <button className="add-trade-button" onClick={handleAddTrade}>
                <Plus size={16} />
                Add Trade
              </button>
            </div>
            <div className="trade-table-container">
              <table>
                <thead>
                  <tr>
                    <th>Trade Type</th>
                    <th>Risk</th>
                    <th>Reward</th>
                    <th>Win/Loss</th>
                    <th>R:R</th>
                    <th>Date</th>
                    <th>Notes</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {trades.map((trade) => (
                    <tr key={trade.id}>
                      <td>
                        <input
                          type="text"
                          value={trade.type}
                          onChange={(e) =>
                            handleTradeChange(trade.id, "type", e.target.value)
                          }
                          placeholder="e.g., Breakout"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={trade.risk}
                          onChange={(e) =>
                            handleTradeChange(trade.id, "risk", e.target.value)
                          }
                          min="0"
                          step="0.01"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={trade.reward}
                          onChange={(e) =>
                            handleTradeChange(
                              trade.id,
                              "reward",
                              e.target.value
                            )
                          }
                          min="0"
                          step="0.01"
                        />
                      </td>
                      <td>
                        <select
                          value={trade.result}
                          onChange={(e) =>
                            handleTradeChange(
                              trade.id,
                              "result",
                              e.target.value
                            )
                          }
                        >
                          <option value="win">Win</option>
                          <option value="loss">Loss</option>
                        </select>
                      </td>
                      <td>
                        <input
                          type="number"
                          value={trade.rr}
                          onChange={(e) =>
                            handleTradeChange(trade.id, "rr", e.target.value)
                          }
                          disabled
                        />
                      </td>
                      <td>
                        <input
                          type="date"
                          value={trade.timestamp}
                          onChange={(e) =>
                            handleTradeChange(
                              trade.id,
                              "timestamp",
                              e.target.value
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={trade.notes}
                          onChange={(e) =>
                            handleTradeChange(trade.id, "notes", e.target.value)
                          }
                          placeholder="Variables, observations..."
                        />
                      </td>
                      <td>
                        <button
                          className="remove-trade"
                          onClick={() => handleRemoveTrade(trade.id)}
                          aria-label="Remove trade"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StratBuilder;
